// components/ui/SuspensionNoticeOverlay.js
// Session 37 — Calque plein écran affiché avant suspension temporaire
//
// Props :
//   - notice: { remaining_seconds, text?, until_hhmm? } | null
//   - onExpire: callback appelé quand le compteur atteint 0 (→ force logout)

import React, { useEffect, useState, useRef } from 'react';
import { Wrench, Sparkles } from 'lucide-react';

const DEFAULT_TEXT = `Le super agent programmeur ITRIANPIC procède à une réparation en temps réel. Désolé pour l'interruption : durée max 15 min. Mais pour vous qui compressez le temps, ça sera probablement 3 min.`;

const SuspensionNoticeOverlay = ({ notice, onExpire }) => {
  const [countdown, setCountdown] = useState(notice?.remaining_seconds || 0);
  const expiredFiredRef = useRef(false);

  // Sync countdown when a new notice arrives
  useEffect(() => {
    if (notice?.remaining_seconds != null) {
      setCountdown(notice.remaining_seconds);
      expiredFiredRef.current = false;
    }
  }, [notice?.remaining_seconds]);

  // Local tick every 1s
  useEffect(() => {
    if (!notice) return;
    if (countdown <= 0) {
      if (!expiredFiredRef.current) {
        expiredFiredRef.current = true;
        if (typeof onExpire === 'function') onExpire();
      }
      return;
    }
    const t = setTimeout(() => setCountdown(c => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [countdown, notice, onExpire]);

  if (!notice) return null;

  const text = notice.text && notice.text.trim().length > 0 ? notice.text : DEFAULT_TEXT;
  const total = notice.remaining_seconds || 30;
  const progress = Math.max(0, Math.min(100, ((total - countdown) / total) * 100));

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(5, 8, 20, 0.82)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px',
        animation: 'suspensionFadeIn 0.35s ease-out',
      }}
    >
      <style>{`
        @keyframes suspensionFadeIn {
          from { opacity: 0; backdrop-filter: blur(0); }
          to   { opacity: 1; backdrop-filter: blur(8px); }
        }
        @keyframes suspensionGear {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes suspensionPulse {
          0%, 100% { transform: scale(1); opacity: 0.9; }
          50%      { transform: scale(1.08); opacity: 1; }
        }
        @keyframes suspensionSparkle {
          0%, 100% { opacity: 0.4; transform: scale(0.95) rotate(0deg); }
          50%      { opacity: 1; transform: scale(1.1) rotate(20deg); }
        }
      `}</style>

      <div style={{
        maxWidth: '560px', width: '100%',
        background: 'linear-gradient(180deg, #12121e 0%, #0e0e18 100%)',
        border: '1px solid rgba(139, 92, 246, 0.35)',
        borderRadius: '18px',
        padding: '40px 36px 32px',
        boxShadow: '0 0 60px rgba(139, 92, 246, 0.25), 0 20px 50px rgba(0,0,0,0.6)',
        color: '#e8e8f0',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Sparkle en haut à droite */}
        <div style={{
          position: 'absolute', top: 16, right: 18,
          color: '#a78bfa',
          animation: 'suspensionSparkle 2.2s ease-in-out infinite',
        }}>
          <Sparkles size={18} strokeWidth={2.2} />
        </div>

        {/* Icône Wrench qui tourne */}
        <div style={{
          width: 78, height: 78,
          margin: '0 auto 20px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(139,92,246,0.22), rgba(59,130,246,0.15))',
          border: '2px solid rgba(139,92,246,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'suspensionPulse 2s ease-in-out infinite',
          boxShadow: '0 0 30px rgba(139,92,246,0.35)',
        }}>
          <div style={{ animation: 'suspensionGear 4s linear infinite', display: 'flex' }}>
            <Wrench size={36} color="#c4b5fd" strokeWidth={2} />
          </div>
        </div>

        {/* Titre */}
        <h2 style={{
          margin: '0 0 16px',
          fontSize: '20px', fontWeight: 700,
          color: '#c4b5fd',
          letterSpacing: '0.5px',
        }}>
          Maintenance en cours
        </h2>

        {/* Message principal */}
        <p style={{
          margin: '0 0 24px',
          fontSize: '15px',
          lineHeight: 1.7,
          color: '#d0d0dc',
          whiteSpace: 'pre-wrap',
        }}>
          {text}
        </p>

        {/* Barre de progression + compteur */}
        <div style={{
          width: '100%',
          height: '4px',
          background: 'rgba(139,92,246,0.12)',
          borderRadius: '2px',
          overflow: 'hidden',
          marginBottom: '12px',
        }}>
          <div style={{
            width: `${progress}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #8b5cf6, #3b82f6)',
            boxShadow: '0 0 10px rgba(139,92,246,0.6)',
            transition: 'width 1s linear',
          }} />
        </div>

        <div style={{
          fontSize: '12px',
          color: '#8888a0',
          letterSpacing: '0.3px',
        }}>
          Déconnexion dans <strong style={{ color: '#c4b5fd', fontVariantNumeric: 'tabular-nums' }}>{countdown}s</strong>
          {notice.until_hhmm && (
            <span style={{ opacity: 0.7 }}> · retour prévu ~ {notice.until_hhmm}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuspensionNoticeOverlay;
